<template>
  <div :class="['tag-filter-container', { 'side-spacing': sideSpacing }]">
    <div class="tag-filter-header">
      <FriedH4>{{ t('dr_filter_on_categories') }} ({{ categories.length }})</FriedH4>
      <FriedButton
        v-if="selectedTags.length > 0"
        compact
        data-external="content-tags-clear-filter-btn"
        button-type="secondary"
        @click.stop="handleClear"
      >
        {{ t('clear') }}
      </FriedButton>
    </div>
    <div ref="filterContent" :class="['filter-content', { compact }]">
      <div class="categories">
        <CategoryButton
          v-for="category in filteredCategories"
          :key="category.id"
          :external-id="`content-tags-category-btn-${category.id}`"
          :active="isCategorySelected(category)"
          @click="handleToggleSelectCategory(category)"
        >
          {{ category.name }} ({{ ContentTagHelper.getCategoryTagCount(category) }})
        </CategoryButton>
      </div>
      <div
        v-for="selectedCategory in sortedSelectedCategories"
        :key="selectedCategory.id"
        class="category-tag-container"
      >
        <FriedH5>{{ selectedCategory.name }}</FriedH5>
        <div class="tags">
          <Tag
            v-for="tag in selectedCategory.tags"
            :key="tag.id"
            :external-id="`content-tags-selected-category-tags-${tag.id}`"
            :active="isTagSelected(tag.id)"
            @click="handleToggleSelectTag(tag.id)"
            >{{ tag.name }}
          </Tag>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { useSelectTags } from '../composables/select-tags.composable';
import type { ContentTagCategory } from '../types';
import ContentTagHelper from '../helpers/content-tag.helper';
import CategoryButton from './CategoryButton.vue';
import Tag from './Tag.vue';

const {
  categories,
  sideSpacing = true,
  tags = [],
  compact = false,
} = defineProps<{
  categories: ContentTagCategory[];
  sideSpacing?: boolean;
  compact?: boolean;
  tags?: string[];
}>();

const emit = defineEmits<{
  'update-tags': [string[]];
}>();

const {
  selectedTags,
  sortedSelectedCategories,
  filteredCategories,
  handleToggleSelectCategory,
  isCategorySelected,
  isTagSelected,
  handleClear,
} = useSelectTags(emit, categories, tags);

const handleToggleSelectTag = (tagId: string) => {
  if (isTagSelected(tagId)) {
    selectedTags.value = selectedTags.value.filter(id => id !== tagId);
  } else {
    const category = categories.find(category => category.tags?.some(tag => tag.id === tagId));
    const tagIds = category?.tags?.map(tag => tag.id);
    selectedTags.value = selectedTags.value.filter(id => !tagIds?.includes(id));
    selectedTags.value = [tagId, ...selectedTags.value];
  }
  emit('update-tags', selectedTags.value);
};
</script>

<style lang="scss" scoped>
.tag-filter-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-100);
  margin: 0;

  .tag-filter-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 2.5rem;
  }

  .categories {
    display: flex;
    gap: var(--spacing-50);
    margin-bottom: var(--spacing-100);
    flex-wrap: wrap;
  }

  .category-tag-container {
    border-top: 1px solid var(--gray-92);
    padding: var(--spacing-100) 0;

    .heading-5 {
      margin-bottom: var(--spacing-50);
      color: var(--text-gray);
    }

    .tags {
      display: flex;
      gap: var(--spacing-50);
      flex-wrap: wrap;
    }
  }

  &.side-spacing {
    .tag-filter-header {
      padding: 0 var(--spacing-50);
    }

    .categories {
      padding: 0 var(--spacing-50);
    }

    .category-tag-container {
      margin: 0 var(--spacing-50);
    }

    .heading-4 {
      color: var(--text-gray);
    }
  }

  .filter-content {
    max-height: 350px;
    overflow-y: auto;

    &.compact {
      max-height: 270px;
    }
  }
}
</style>
